/**
 * Constants and types for BlueYonder integration
 */

import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Log stamp for consistent logging across the application
export const BLUE_YONDER_LOG_STAMP = "- BlueYonder Integration -";

// BlueYonder locations URL for customers to find drop-off points
export const BLUE_YONDER_LOCATIONS_URL = "https://www.blueyonder.com/locations";

// BlueYonder webhook event types for processing
export enum WebhookEventType {
  DROPPED_OFF = "RETURN_EVENT-DROPPED_OFF",
  DESPATCHED_FROM_PUDO = "RETURN_EVENT-DESPATCHED_FROM_PUDO",
  SCANNED_TO_SORT_CONTAINER = "RETURN_EVENT-SCANNED_TO_SORT_CONTAINER",
  ON_ITS_WAY = "RETURN_EVENT-ON_ITS_WAY",
  ARRIVED_AT_DESTINATION = "RETURN_EVENT-ARRIVED_AT_DESTINATION",
  DELIVERED_AT_PROCESSING_FACILITY = "RETURN_EVENT-DELIVERED_AT_PROCESSING_FACILITY",
}

export const blueYonderWebhookEventTypeMessages: Record<
  WebhookEventType,
  string
> = {
  [WebhookEventType.DROPPED_OFF]: "Package dropped off at FedEx location",
  [WebhookEventType.DESPATCHED_FROM_PUDO]:
    "Package dispatched from FedEx pickup/drop-off center",
  [WebhookEventType.SCANNED_TO_SORT_CONTAINER]:
    "Package scanned to sort container",
  [WebhookEventType.ON_ITS_WAY]: "Package on its way",
  [WebhookEventType.ARRIVED_AT_DESTINATION]: "Package arrived at destination",
  [WebhookEventType.DELIVERED_AT_PROCESSING_FACILITY]:
    "Package delivered at processing facility",
};

export const blueYonderLocationSchema = z.object({
  blueyonderLocationId: z.string(),
  locationName: z.string(),
});

export const blueYonderIntegrationInputSchema = z.object({
  retailerCompanyId: z.string(),
  locations: z.array(blueYonderLocationSchema).optional(),
  processingEvent: createEnum(WebhookEventType).optional(),
  testMode: z.boolean().optional(),
  radiusMiles: z.number().min(1).optional(),
  upcharge: z.number().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  locationsText: z.string().optional(),
});

export type BlueYonderIntegrationInput = z.infer<
  typeof blueYonderIntegrationInputSchema
>;

export enum BlueYonderIssueCode {
  NOT_HEADED_TO_BLUEYONDER = "not-headed-to-blueyonder",
  MISSING_SKUS = "missing-skus",
  PENDING_MANUAL_REVIEW = "pending-manual-review",
  NO_SHIPMENTS_PRESENT = "no-shipments-present",
  INTERNAL_ERROR = "internal-error",
  SUCCESS = "success",
}

export const blueYonderIssueCodeToDisplayText: Record<
  BlueYonderIssueCode,
  string
> = {
  [BlueYonderIssueCode.NOT_HEADED_TO_BLUEYONDER]: "not headed to BlueYonder",
  [BlueYonderIssueCode.MISSING_SKUS]: "missing SKUs",
  [BlueYonderIssueCode.PENDING_MANUAL_REVIEW]: "pending manual review",
  [BlueYonderIssueCode.NO_SHIPMENTS_PRESENT]: "no shipments present",
  [BlueYonderIssueCode.INTERNAL_ERROR]: "internal error",
  [BlueYonderIssueCode.SUCCESS]: "success",
};

export enum DropoffReturnStatus {
  CREATED = "created",
  DROPPED_OFF = "RETURN_EVENT-DROPPED_OFF",
  DESPATCHED_FROM_PUDO = "RETURN_EVENT-DESPATCHED_FROM_PUDO",
  SCANNED_TO_SORT_CONTAINER = "RETURN_EVENT-SCANNED_TO_SORT_CONTAINER",
  ON_ITS_WAY = "RETURN_EVENT-ON_ITS_WAY",
  ARRIVED_AT_DESTINATION = "RETURN_EVENT-ARRIVED_AT_DESTINATION",
  DELIVERED_AT_PROCESSING_FACILITY = "RETURN_EVENT-DELIVERED_AT_PROCESSING_FACILITY",
}

export const dropoffReturnStatusToDisplayText: Record<
  DropoffReturnStatus,
  string
> = {
  [DropoffReturnStatus.CREATED]: "Awaiting drop-off",
  [DropoffReturnStatus.DROPPED_OFF]: "Dropped off",
  [DropoffReturnStatus.DESPATCHED_FROM_PUDO]: "Dispatched",
  [DropoffReturnStatus.SCANNED_TO_SORT_CONTAINER]: "Scanned to sort container",
  [DropoffReturnStatus.ON_ITS_WAY]: "On its way",
  [DropoffReturnStatus.ARRIVED_AT_DESTINATION]: "Arrived",
  [DropoffReturnStatus.DELIVERED_AT_PROCESSING_FACILITY]:
    "Delivered at processing facility",
};

/** Earliest-to-latest webhook events. The two terminal events are peers. */
export const WEBHOOK_EVENT_ORDER: readonly WebhookEventType[] = [
  WebhookEventType.DROPPED_OFF,
  WebhookEventType.DESPATCHED_FROM_PUDO,
  WebhookEventType.SCANNED_TO_SORT_CONTAINER,
  WebhookEventType.ON_ITS_WAY,
  WebhookEventType.ARRIVED_AT_DESTINATION,
  WebhookEventType.DELIVERED_AT_PROCESSING_FACILITY,
];

const TERMINAL_WEBHOOK_EVENTS = new Set<WebhookEventType>([
  WebhookEventType.ARRIVED_AT_DESTINATION,
  WebhookEventType.DELIVERED_AT_PROCESSING_FACILITY,
]);

function webhookEventRank(event: WebhookEventType): number {
  if (TERMINAL_WEBHOOK_EVENTS.has(event)) {
    return WEBHOOK_EVENT_ORDER.indexOf(WebhookEventType.ARRIVED_AT_DESTINATION);
  }
  return WEBHOOK_EVENT_ORDER.indexOf(event);
}

/** True when the received event is at or after the configured processing event. */
export function isEventOnOrAfter(
  targetEvent: WebhookEventType,
  receivedEvent: WebhookEventType,
): boolean {
  const targetRank = webhookEventRank(targetEvent);
  const receivedRank = webhookEventRank(receivedEvent);
  return targetRank >= 0 && receivedRank >= targetRank;
}

/** One dropdown choice covers both peer terminal webhooks. */
export const BLUE_YONDER_PROCESSING_EVENT_OPTIONS: readonly {
  id: string;
  value: WebhookEventType | null;
  text: string;
}[] = [
  { id: "None", value: null, text: "None (Manual Processing)" },
  {
    id: WebhookEventType.DROPPED_OFF,
    value: WebhookEventType.DROPPED_OFF,
    text: "Dropped Off",
  },
  {
    id: WebhookEventType.DESPATCHED_FROM_PUDO,
    value: WebhookEventType.DESPATCHED_FROM_PUDO,
    text: "Dispatched from BlueYonder",
  },
  {
    id: WebhookEventType.ARRIVED_AT_DESTINATION,
    value: WebhookEventType.ARRIVED_AT_DESTINATION,
    text: "Arrived at Destination",
  },
];

export const BLUE_YONDER_PROCESSING_EVENT_DESCRIPTION =
  "Select which webhook event should trigger automatic processing. Arrived at Destination also processes delivery at a processing facility.";

/** Stored facility deliveries select the same dropdown choice as arrival. */
export function canonicalProcessingEvent(
  event: WebhookEventType | null | undefined,
): WebhookEventType | null | undefined {
  if (event === WebhookEventType.DELIVERED_AT_PROCESSING_FACILITY) {
    return WebhookEventType.ARRIVED_AT_DESTINATION;
  }
  return event;
}

export const BLUEYONDER_DATADOG_METRIC = "redo.return.blueyonder.createRma";

export enum BlueYonderMetricTags {
  OUTCOME = "redo.return.blueyonder.outcome",
  NUM_SHIPMENT_GROUPS_PROCESSED = "redo.return.blueyonder.numShipmentGroupsProcessed",
}
